import { TEAM_DISPLAY, TeamSide } from "../constants/teams";
import { GameTeam } from "../types/gameFull";

const SIDES: TeamSide[] = ["home", "away"];

export type TeamDisplay = { color: string; textColor: string };

export type TeamDisplays = Record<TeamSide, TeamDisplay>;

// Omnibus returns saved colors as { homeColor, awayColor } (its serialization
// alias), though the spec types them as { home, away }; either is read
type SavedTeamColors = {
  home?: string | null;
  away?: string | null;
  homeColor?: string | null;
  awayColor?: string | null;
};

const HEX_COLOR = /^#[0-9a-f]{6}([0-9a-f]{2})?$/i;

/** Black or white, whichever reads better on `hex` (WCAG relative luminance). */
export const getReadableTextColor = (hex: string) => {
  const [r, g, b] = [1, 3, 5].map((start) => {
    const channel = parseInt(hex.slice(start, start + 2), 16) / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.179 ? "#000000" : "#FFFFFF";
};

/** The game service's teams, which say who is home now. */
export type GameTeamsBySide = Record<
  TeamSide,
  Pick<GameTeam, "id" | "battingHalf">
>;

/**
 * Each team's color from the game's saved team colors, falling back to the
 * default home blue / away gold when a color is missing or isn't "#RRGGBB".
 *
 * With the game service's `teams`, a color stays with its team when the home
 * and away teams are swapped: a saved color is found by team id (the omnibus
 * side can lag the game service's, or not swap at all), and a default goes by
 * the half the team bats in, which a mid-game swap doesn't change.
 */
export const toTeamDisplays = (
  saved: unknown,
  /** The omnibus game's team ids, which the saved colors are keyed by */
  savedTeamIds?: Record<TeamSide, string>,
  teams?: GameTeamsBySide,
): TeamDisplays => {
  const colors = saved as SavedTeamColors | null | undefined;
  const savedColors: Record<TeamSide, string | null | undefined> = {
    home: colors?.homeColor ?? colors?.home,
    away: colors?.awayColor ?? colors?.away,
  };

  const display = (side: TeamSide): TeamDisplay => {
    const team = teams?.[side];
    const savedSide =
      (team?.id &&
        SIDES.find((s) => savedTeamIds?.[s] && savedTeamIds[s] === team.id)) ||
      side;
    const value = savedColors[savedSide];
    if (!value || !HEX_COLOR.test(value)) {
      if (!team) return TEAM_DISPLAY[side];
      return TEAM_DISPLAY[team.battingHalf === "top" ? "away" : "home"];
    }
    // Alpha is dropped: badges and text need the solid color
    const color = value.slice(0, 7).toUpperCase();
    return { color, textColor: getReadableTextColor(color) };
  };
  return { home: display("home"), away: display("away") };
};

/** The away team bats in the top of the inning, the home team pitches. */
export const getRoleColors = (
  isTopOfInning: boolean,
  awayColor: string,
  homeColor: string,
) => ({
  hitterColor: isTopOfInning ? awayColor : homeColor,
  pitcherColor: isTopOfInning ? homeColor : awayColor,
});
